import { inject, Injectable } from '@angular/core';
import { from, map, of, switchMap, type Observable } from 'rxjs';
import { COMMO_SHARED_SUPABASE_CLIENT } from '../commo-shared-data-access.config';
import {
  FilterIs,
  GetEventsForNoticeGQL,
  ListCommunityEventsGQL,
  OrderByDirection,
  type Community_EventsFilter,
} from '../graphql/generated/services';
import type {
  CommunityEvent,
  ListCommunityEventsParams,
  UpsertCommunityEventInput,
} from '../models/community-event.model';

type EventRow = {
  id: string;
  community_id: string;
  title: string;
  start_date: string;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  notice_id: string;
  start_sort: string;
};

function mapRow(row: EventRow): CommunityEvent {
  return {
    id: row.id,
    communityId: row.community_id,
    title: row.title,
    startDate: row.start_date,
    startTime: row.start_time,
    endDate: row.end_date,
    endTime: row.end_time,
    noticeId: row.notice_id,
    startSort: row.start_sort,
  };
}

/** ローカル予定の `YYYY-MM-DDTHH:mm:ss` と並べ替えを揃える。 */
function startSortFromGraphQl(value: unknown, startDate: string): string {
  if (value == null || value === '') return `${startDate}T00:00:00`;
  const raw = String(value).replace(' ', 'T');
  return raw.length >= 19 ? raw.slice(0, 19) : `${startDate}T00:00:00`;
}

function escapeIlike(raw: string): string {
  return raw.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

/** `coalesce(end_date, start_date) >= today` */
function ongoingOrFuture(today: string): Community_EventsFilter {
  return {
    or: [
      { end_date: { gte: today } },
      {
        and: [
          { end_date: { is: FilterIs.Null } },
          { start_date: { gte: today } },
        ],
      },
    ],
  };
}

/** `coalesce(end_date, start_date) < today` */
function endedBeforeToday(today: string): Community_EventsFilter {
  return {
    or: [
      { end_date: { lt: today } },
      {
        and: [
          { end_date: { is: FilterIs.Null } },
          { start_date: { lt: today } },
        ],
      },
    ],
  };
}

/**
 * `(start_sort, id)` と同じ順の keyset。
 * UUID フィルタに大小が無いので、DB の `list_key`（秒精度 + id）と比較する。
 */
function eventListKey(sort: string, id: string): string {
  return `${sort.replace(' ', 'T').slice(0, 19)}|${id}`;
}

/** pg_graphql は `first` をこの件数で黙って切る。超えた分はカーソルで足す。 */
const GRAPHQL_COLLECTION_PAGE_MAX = 30;

function mapEventNode(node: {
  id: string;
  community_id: string;
  title: string;
  start_date: string;
  start_time?: string | null;
  end_date?: string | null;
  end_time?: string | null;
  notice_id: string;
  start_sort?: unknown;
}): CommunityEvent {
  return mapRow({
    id: node.id,
    community_id: node.community_id,
    title: node.title,
    start_date: node.start_date,
    start_time: node.start_time ?? null,
    end_date: node.end_date ?? null,
    end_time: node.end_time ?? null,
    notice_id: node.notice_id,
    start_sort: startSortFromGraphQl(node.start_sort, node.start_date),
  });
}

function listEventsFilter(
  params: ListCommunityEventsParams,
): Community_EventsFilter {
  const search = params.search?.trim() ?? '';
  const parts: Community_EventsFilter[] = [
    { community_id: { eq: params.communityId } },
  ];
  if (search) {
    parts.push({ title: { ilike: `%${escapeIlike(search)}%` } });
  }
  const fromToday = Boolean(params.fromToday) && search.length === 0;
  const today = params.today ?? null;
  if (fromToday && today) {
    parts.push(
      params.direction === 'backward'
        ? endedBeforeToday(today)
        : ongoingOrFuture(today),
    );
  }
  if (params.cursorSort && params.cursorId) {
    const key = eventListKey(params.cursorSort, params.cursorId);
    parts.push(
      params.direction === 'backward'
        ? { list_key: { lt: key } }
        : { list_key: { gt: key } },
    );
  }
  return { and: parts };
}

@Injectable({ providedIn: 'root' })
export class CommunityEventsService {
  private readonly supabase = inject(COMMO_SHARED_SUPABASE_CLIENT);
  private readonly getEventsForNoticeGql = inject(GetEventsForNoticeGQL);
  private readonly listCommunityEventsGql = inject(ListCommunityEventsGQL);

  listEvents(params: ListCommunityEventsParams): Observable<CommunityEvent[]> {
    const target = Math.max(1, Math.min(params.limit ?? 20, 50));
    const backward = params.direction === 'backward';
    const filter = listEventsFilter(params);
    const orderBy = [
      {
        list_key: backward
          ? OrderByDirection.DescNullsLast
          : OrderByDirection.AscNullsLast,
      },
    ];
    const seenCursors = new Set<string>();
    const loadPage = (
      after: string | null,
      remaining: number,
    ): Observable<CommunityEvent[]> =>
      this.listCommunityEventsGql
        .fetch({
          variables: {
            first: Math.min(remaining, GRAPHQL_COLLECTION_PAGE_MAX),
            ...(after ? { after } : {}),
            filter,
            orderBy,
          },
          fetchPolicy: 'network-only',
        })
        .pipe(
          switchMap((result) => {
            if (result.error) throw new Error(result.error.message);
            const connection = result.data?.community_eventsCollection;
            const page = (connection?.edges ?? [])
              .map((edge) => edge?.node)
              .filter(
                (node): node is NonNullable<typeof node> =>
                  typeof node?.id === 'string' && node.id.length > 0,
              )
              .map((node) => mapEventNode(node));
            const endCursor = connection?.pageInfo.endCursor ?? null;
            if (
              page.length < remaining &&
              connection?.pageInfo.hasNextPage &&
              endCursor &&
              !seenCursors.has(endCursor)
            ) {
              seenCursors.add(endCursor);
              return loadPage(endCursor, remaining - page.length).pipe(
                map((rest) => [...page, ...rest]),
              );
            }
            return of(page.slice(0, remaining));
          }),
        );
    return loadPage(null, target);
  }

  listEventsForNotice(noticeId: string): Observable<CommunityEvent[]> {
    const seenCursors = new Set<string>();
    const loadPage = (after: string | null): Observable<CommunityEvent[]> =>
      this.getEventsForNoticeGql
        .fetch({
          variables: {
            notice_id: noticeId,
            ...(after ? { after } : {}),
          },
          fetchPolicy: 'network-only',
        })
        .pipe(
          switchMap((result) => {
            if (result.error) throw new Error(result.error.message);
            const connection = result.data?.community_eventsCollection;
            const page = (connection?.edges ?? [])
              .map((edge) => edge?.node)
              .filter(
                (node): node is NonNullable<typeof node> =>
                  typeof node?.id === 'string' && node.id.length > 0,
              )
              .map((node) => mapEventNode(node));
            const endCursor = connection?.pageInfo.endCursor ?? null;
            if (
              connection?.pageInfo.hasNextPage &&
              endCursor &&
              !seenCursors.has(endCursor)
            ) {
              seenCursors.add(endCursor);
              return loadPage(endCursor).pipe(
                map((rest) => [...page, ...rest]),
              );
            }
            return of(page);
          }),
        );
    return loadPage(null);
  }

  upsertEvent(input: UpsertCommunityEventInput): Observable<string> {
    return from(
      this.supabase.rpc('upsert_community_event', {
        p_community_id: input.communityId,
        p_title: input.title.trim(),
        p_start_date: input.startDate,
        p_start_time: input.startTime ?? null,
        p_end_date: input.endDate ?? null,
        p_end_time: input.endTime ?? null,
        p_notice_id: input.noticeId,
        p_id: input.id ?? null,
      }),
    ).pipe(
      map(({ data, error }) => {
        if (error) throw new Error(error.message);
        const id = data as string | null;
        if (!id) throw new Error('Failed to save event.');
        return id;
      }),
    );
  }

  deleteEvent(id: string): Observable<void> {
    return from(this.supabase.rpc('delete_community_event', { p_id: id })).pipe(
      map(({ error }) => {
        if (error) throw new Error(error.message);
      }),
    );
  }
}
